import React from 'react';
import StatusIcon from 'src/components/StatusIcon';
import {
  CancelOutlined,
  Edit,
  Send,
  Assignment,
  UndoOutlined,
  FileCopy,
  CheckCircle,
  AccountBalance
} from '@mui/icons-material';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import AddIcon from '@mui/icons-material/Add';
import UserType from 'src/utils/constants/auth';
import { GracePeriodValues } from './constants/distributors';
import { IconStatusAndColor } from './constants/common';
import ContractStatus, {
  AmendingAgreementSignStatus,
  AutomaticDebitRequestSignStatus,
  ContractActionsOptions,
  ContractCessionSignStatus,
  ContractEarlyTerminationSignStatus,
  ContractsEventsStatus,
  ContractPaymentForm
} from './constants/contracts';
import momentLocalTimezone from './momentLocalTimezone';

export const getContractStatusIcon = contract => {
  switch (contract.status) {
    case ContractStatus.IN_PROGRESS:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.SIGNED"
          type={IconStatusAndColor.OK.status}
        />
      );

    case ContractStatus.PENDING_SIGNATURE:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.PENDING"
          type={IconStatusAndColor.WARNING.status}
        />
      );
    case ContractStatus.SIGNED:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.PENDING"
          type={IconStatusAndColor.WARNING.status}
        />
      );
    case ContractStatus.DRAFT:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.DRAFT"
          type={IconStatusAndColor.NONE.status}
        />
      );
    case ContractStatus.CEDED:
      return (
        <StatusIcon
          description="COMMON.CEDED"
          type={IconStatusAndColor.NONE.status}
        />
      );
    default:
      return (
        <StatusIcon
          description="COMMON.NO_DATA"
          type={IconStatusAndColor.NONE.status}
        />
      );
  }
};

export const getAutomaticDebitIcon = contract => {
  if (!contract.isAutomaticDebitEnabled)
    return (
      <StatusIcon
        description="COMMON.INACTIVE"
        type={IconStatusAndColor.ERROR.status}
      />
    );
  return (
    <StatusIcon
      description="COMMON.ACTIVE"
      type={IconStatusAndColor.OK.status}
    />
  );
};

export const getAutomaticDebitText = contract => {
  if (!contract.isAutomaticDebitEnabled)
    return 'CONTRACT_VIEW.AUTOMATIC_DEBIT_DISABLED';
  return 'CONTRACT_VIEW.AUTOMATIC_DEBIT_ENABLED';
};

export const selectBackGroundColorFromContractStatus = contract => {
  switch (contract.status) {
    case ContractStatus.IN_PROGRESS:
      return 'rgba(40,167,69,0.1)';
    case ContractStatus.PENDING_SIGNATURE:
      return 'rgba(255,193,7,0.1)';
    case ContractStatus.SIGNED:
      return 'rgba(255,193,7,0.1)';
    case ContractStatus.DRAFT:
      return 'rgba(198,198,196,0.21)';
    case ContractStatus.CEDED:
      return 'rgba(198,198,196,0.21)';
    default:
      return 'rgba(198,198,196,0.21)';
  }
};

export const getContractSignedStatus = contract => {
  if (!contract.contractDoc)
    return (
      <StatusIcon
        description="COMMON.NO_DATA"
        type={IconStatusAndColor.NONE.status}
      />
    );
  switch (contract.status) {
    case ContractStatus.IN_PROGRESS:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.SIGNED"
          type={IconStatusAndColor.OK.status}
        />
      );

    case ContractStatus.PENDING_SIGNATURE:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.PENDING"
          type={IconStatusAndColor.WARNING.status}
        />
      );
    case ContractStatus.SIGNED:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.SIGNED"
          type={IconStatusAndColor.OK.status}
        />
      );
    case ContractStatus.DRAFT:
      return (
        <StatusIcon
          description="COMMON.NO_DATA"
          type={IconStatusAndColor.NONE.status}
        />
      );
    default:
      return (
        <StatusIcon
          description="COMMON.NO_DATA"
          type={IconStatusAndColor.NONE.status}
        />
      );
  }
};

export const getContractCessionSignStatus = contract => {
  switch (contract.contractCession.signStatus) {
    case ContractCessionSignStatus.DRAFT:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.DRAFT"
          type={IconStatusAndColor.NONE.status}
        />
      );
    case ContractCessionSignStatus.PENDING_SIGNATURE:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.PENDING"
          type={IconStatusAndColor.WARNING.status}
        />
      );
    case ContractCessionSignStatus.SIGNED:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.SIGNED"
          type={IconStatusAndColor.OK.status}
        />
      );
    default:
      return (
        <StatusIcon
          description="COMMON.NO_DATA"
          type={IconStatusAndColor.NONE.status}
        />
      );
  }
};

export const getContractEarlyTerminationSignStatus = contract => {
  switch (contract.contractEarlyTermination.signStatus) {
    case ContractEarlyTerminationSignStatus.PENDING_SIGNATURE:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.PENDING"
          type={IconStatusAndColor.WARNING.status}
        />
      );
    case ContractEarlyTerminationSignStatus.SIGNED:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.SIGNED"
          type={IconStatusAndColor.OK.status}
        />
      );
    default:
      return (
        <StatusIcon
          description="COMMON.NO_DATA"
          type={IconStatusAndColor.NONE.status}
        />
      );
  }
};

export const getContractEquipmentDeliveryActSignStatus = contract => {
  if (contract?.equipmentDeliveryAct?.documentUrl) {
    return (
      <StatusIcon
        description="COMMON.DOCUMENT_UPLOADED_SUCCESSFULLY"
        type={IconStatusAndColor.OK.status}
      />
    );
  }
  return (
    <StatusIcon
      description="COMMON.NO_DATA"
      type={IconStatusAndColor.NONE.status}
    />
  );
};

export const getAmendingAgreementSignStatus = contract => {
  switch (contract.lastCapitalContribution.signStatus) {
    case AmendingAgreementSignStatus.PENDING_SIGNATURE:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.PENDING"
          type={IconStatusAndColor.WARNING.status}
        />
      );
    case AmendingAgreementSignStatus.SIGNED:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.SIGNED"
          type={IconStatusAndColor.OK.status}
        />
      );
    default:
      return (
        <StatusIcon
          description="COMMON.NO_DATA"
          type={IconStatusAndColor.NONE.status}
        />
      );
  }
};

export const getAutomaticDebitRequestSignStatus = contract => {
  switch (contract.automaticDebitRequest.signStatus) {
    case AutomaticDebitRequestSignStatus.PENDING_TO_SIGN:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.PENDING"
          type={IconStatusAndColor.WARNING.status}
        />
      );
    case AutomaticDebitRequestSignStatus.SIGNED:
      return (
        <StatusIcon
          description="ENUMS.DOCUMENT_STATUS.SIGNED"
          type={IconStatusAndColor.OK.status}
        />
      );
    default:
      return (
        <StatusIcon
          description="COMMON.NO_DATA"
          type={IconStatusAndColor.NONE.status}
        />
      );
  }
};

export const shouldShowSendToSignAutomaticDebitRequest = contract => {
  return (
    contract.automaticDebitRequest &&
    contract.automaticDebitRequest.signStatus ===
      AutomaticDebitRequestSignStatus.DRAFT
  );
};

export const getOnSendToSignHandler = (
  contract,
  handleSendToSignAutomaticDebitRequest
) => {
  return shouldShowSendToSignAutomaticDebitRequest(contract)
    ? handleSendToSignAutomaticDebitRequest
    : null;
};

export const getGracePeriodFromMonthDiff = diff => {
  switch (diff) {
    case 0:
      return GracePeriodValues.SAME_DAY_AS_CONTRACT_STARTS;
    case 1:
      return GracePeriodValues.ZERO;
    case 2:
      return GracePeriodValues.THIRTY;
    case 3:
      return GracePeriodValues.SIXTY;
    case 4:
      return GracePeriodValues.NINETY;
    default:
      return GracePeriodValues.SAME_DAY_AS_CONTRACT_STARTS;
  }
};

export const ContractActionMenuOptions = [
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.SEND_TO_DIGITAL_SIGN',
    key: ContractActionsOptions.SEND_TO_DIGITAL_SIGN,
    icon: <Send />
  },
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.SEND_TO_DRAFT',
    key: ContractActionsOptions.SEND_TO_DRAFT,
    icon: <UndoOutlined />
  },
  {
    label: 'COMMON.ACTIVATE_AUTOMATIC_BILLING',
    key: ContractActionsOptions.ACTIVATE_AUTOMATIC_BILLING,
    icon: <PaymentIcon />
  },
  {
    label: 'COMMON.DEACTIVATE_AUTOMATIC_BILLING',
    key: ContractActionsOptions.DESACTIVATE_AUTOMATIC_BILLING,
    icon: <CreditCardOffIcon />
  },
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.EDIT_CONTRACT_DATES',
    key: ContractActionsOptions.EDIT_CONTRACT_DATES,
    icon: <Edit />
  },
  {
    label: 'Generar formato de domiciliación',
    key: ContractActionsOptions.GENERATE_AUTOMATIC_REQUEST_DOCUMENT,
    icon: <AddIcon />
  },
  {
    label: 'CONTRACT_VIEW.REGENERATE_AUTOMATIC_REQUEST_DOCUMENT',
    key: ContractActionsOptions.REGENERATE_AUTOMATIC_REQUEST_DOCUMENT,
    icon: <AddIcon />
  },
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.CANCEL_CONTRACT',
    key: ContractActionsOptions.CANCEL_CONTRACT,
    icon: <CancelOutlined color="error" />
  },
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.CEDE_CONTRACT',
    key: ContractActionsOptions.CEDE_CONTRACT,
    icon: <Assignment />
  },
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.CANCEL_CONTRACT_CESSION',
    key: ContractActionsOptions.CANCEL_CONTRACT_CESSION,
    icon: <CancelOutlined color="error" />
  },
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.RESEND_CONTRACT_CESSION_SIGN_REQUEST',
    key: ContractActionsOptions.RESEND_SIGN_REQUEST_FOR_CONTRACT_CESSION,
    icon: <Send />
  },
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.SEND_CONTRACT_CESSION_TO_SIGN',
    key: ContractActionsOptions.SEND_CONTRACT_CESSION_TO_SIGN,
    icon: <Send />
  },
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.ASSIGN_FIDEICOMISO',
    key: ContractActionsOptions.ASSIGN_FIDEICOMISO,
    icon: <AccountBalance />
  },
  {
    label: 'CONTRACT_VIEW.EARLY_TERMINATION_CONTRACT_FORM',
    key: ContractActionsOptions.TERMINATE_CONTRACT_EARLY,
    icon: <CancelOutlined color="error" />
  },
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.GENERATE_TERMINATION_LETTER',
    key: ContractActionsOptions.GENERATE_TERMINATION_LETTER,
    icon: <FileCopy />
  },
  {
    label: 'CONTRACT_VIEW.CONTRACT_DETAIL.TERMINATE_CONTRACT',
    key: ContractActionsOptions.TERMINATE_CONTRACT,
    icon: <CheckCircle color="success" />
  }
];

export const isContractEligibleForEarlyTermination = (
  contract,
  hasPendingCession,
  hasPendingTermination
) => {
  const isContractCompletedOrPending = contract.clientEvents?.some(
    event =>
      event.status === ContractsEventsStatus.COMPLETED ||
      event.status === ContractsEventsStatus.PENDING
  );

  const currentMonth = momentLocalTimezone();
  const firstRentMonth = momentLocalTimezone(contract.firstRentDate);
  const isFirstRentMonthValid = currentMonth.isSameOrAfter(
    firstRentMonth,
    'month'
  );

  return (
    contract.status === ContractStatus.IN_PROGRESS &&
    momentLocalTimezone(contract.endDate).isAfter(
      momentLocalTimezone().add(1, 'month')
    ) &&
    momentLocalTimezone(contract.endDate).isAfter(momentLocalTimezone()) &&
    isContractCompletedOrPending &&
    !hasPendingTermination &&
    !hasPendingCession &&
    isFirstRentMonthValid
  );
};

export const isContractEligibleForCession = (
  contract,
  hasOpenCession,
  hasPendingTermination
) => {
  return (
    contract.status === ContractStatus.IN_PROGRESS &&
    !hasOpenCession &&
    !hasPendingTermination &&
    momentLocalTimezone(contract.endDate).isAfter(
      momentLocalTimezone().add(1, 'month')
    )
  );
};

export const canGenerateTerminationLetter = endDate => {
  const endDateMoment = momentLocalTimezone(endDate);
  const currentMoment = momentLocalTimezone();
  return (
    endDateMoment.isSame(currentMoment, 'month') ||
    currentMoment.isAfter(endDateMoment)
  );
};

export const isContractEligibleForCancellation = (
  contract,
  hasOpenCession,
  hasPendingTermination,
  role
) => {
  return (
    contract.status === ContractStatus.IN_PROGRESS &&
    role === UserType.SUPER_ADMIN &&
    !hasOpenCession &&
    !hasPendingTermination
  );
};

export const isContractEligibleForTermination = contract => {
  const currentDate = momentLocalTimezone();
  const lastRentDate = momentLocalTimezone(contract.endDate);

  return (
    contract.status === ContractStatus.IN_PROGRESS &&
    lastRentDate.isBefore(currentDate, 'day')
  );
};

export const getMaxDateForSecondRentDate = (
  maxGracePeriod,
  startDate,
  isFirstUpfrontRentModality
) => {
  if (isFirstUpfrontRentModality) {
    const maxValue = Math.max(maxGracePeriod, GracePeriodValues.NINETY);
    return momentLocalTimezone(startDate).add(
      (Number(maxValue) + 30) / 30 < 4 ? (Number(maxValue) + 30) / 30 : 4,
      'months'
    );
  }

  if (maxGracePeriod <= GracePeriodValues.THIRTY) {
    return momentLocalTimezone(startDate).add(2, 'months');
  }

  return momentLocalTimezone(startDate).add(
    (Number(maxGracePeriod) + 30) / 30 < 4
      ? (Number(maxGracePeriod) + 30) / 30
      : 4,
    'months'
  );
};

export const formatPaymentForm = paymentForm => {
  switch (paymentForm) {
    case ContractPaymentForm.BANK_TRANSFER:
      return 'ENUMS.CONTRACT_PAYMENT_FORM.BANK_TRANSFER';
    case ContractPaymentForm.CREDIT_CARD:
      return 'ENUMS.CONTRACT_PAYMENT_FORM.CREDIT_CARD';
    case ContractPaymentForm.AUTOMATIC_DEBIT:
      return 'ENUMS.CONTRACT_PAYMENT_FORM.AUTOMATIC_DEBIT';
    default:
      return '-';
  }
};

export const getPaymentFormOptions = () => [
  {
    key: ContractPaymentForm.AUTOMATIC_DEBIT,
    name: 'CONTRACT_VIEW.CONTRACT_DETAIL.DOMICILED_COLLECTION'
  },
  {
    key: ContractPaymentForm.CREDIT_CARD,
    name: 'CONTRACT_VIEW.CONTRACT_DETAIL.CREDIT_CARD'
  },
  {
    key: ContractPaymentForm.BANK_TRANSFER,
    name: 'CONTRACT_VIEW.CONTRACT_DETAIL.TRANSFER'
  }
];
